import { DOCUMENT } from '@angular/common';
import { RendererFactory2, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ResolveFn } from '@angular/router';
import { Observable, catchError, of, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { Product } from '../product/product.types';
import { ProductsService } from '../product/products.service';
import { CanonicalService } from '../shared/canonical.service';
import { capitalizeWords } from '../shared/common/utils';
import { buildProductJsonLd, buildSeoDescription } from '../shared/seo/product-json-ld';

export const productResolver: ResolveFn<Product | null> = (route): Observable<Product | null> => {
  const productsService = inject(ProductsService);
  const canonicalService = inject(CanonicalService);
  const title = inject(Title);
  const meta = inject(Meta);
  const document = inject(DOCUMENT);
  const renderer = inject(RendererFactory2).createRenderer(null, null);

  const seoUrl = `${route.params['category']}/${route.params['subcategory']}/${route.params['slug']}`;

  return productsService.getProductByUrl(seoUrl, true, false).pipe(
    tap(product => {
      if (!product) return;

      const { baseTitle, baseURL } = environment.config.seo;
      const pageTitle = `${capitalizeWords(seoUrl.replace(/\//g, ' | '))} | ${baseTitle}`;
      title.setTitle(pageTitle);
      meta.updateTag({ property: 'og:title', content: pageTitle });
      meta.updateTag({ property: 'og:url', content: `${baseURL}shop/${seoUrl}` });
      canonicalService.setPageType('product');

      const description = buildSeoDescription(product).substring(0, 160);
      if (description) canonicalService.setPageDescription(description);

      const imageUrl = (product as any).hrImg || product.img;
      if (imageUrl) canonicalService.setPageImage(imageUrl);

      document.head
        .querySelectorAll('script[type="application/ld+json"]')
        .forEach(s => {
          const text = s.textContent || '';
          if (text.includes('"@type":"Product"') || text.includes('"@type": "Product"')) {
            renderer.removeChild(document.head, s);
          }
        });

      const data = buildProductJsonLd(product, imageUrl ? canonicalService.getAbsoluteImageUrl(imageUrl) : null);

      const script = renderer.createElement('script') as HTMLScriptElement;
      script.type = 'application/ld+json';
      script.text = JSON.stringify(data);
      renderer.appendChild(document.head, script);
    }),
    catchError(() => of(null))
  );
};
